import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { normalizeRichEditorHtmlForStorage } from './normalizeRichEditorHtmlForStorage'
import { RichEditorFooterActions } from './RichEditorFooterActions'
import { TipTapToolbar } from './TipTapToolbar'

export { normalizeRichEditorHtmlForStorage }

function useLatestRef<T>(value: T) {
  const r = useRef(value)
  r.current = value
  return r
}

export interface RichEditorProps {
  /** Initial / external HTML (e.g. server-loaded body). */
  html?: string
  /** Called with serialized HTML whenever the document changes. */
  onChangeHtml?: (html?: string) => void
  readOnly?: boolean
  /** Reserved for image upload / asset flow; not used in this TipTap spike. */
  imagePlugin?: boolean
}

const emptyDoc = '<p></p>'

const Shell = styled.div`
  overflow: auto;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  background: ${({ theme }) => theme.color.neutral.color11};
  resize: vertical;
  min-height: 200px;
  max-height: min(70vh, 720px);
`

/** TipTap placeholder + prose typography; theme-driven */
const EditableWrap = styled.div`
  position: relative;
  min-height: 200px;
  padding-bottom: 48px;

  .tiptap {
    min-height: 180px;
    padding: 12px;
    outline: none;
    font-family: ${({ theme }) => theme.font.family.sansSerif};
  }

  .tiptap:focus-visible {
    outline: none;
  }

  .tiptap p.is-editor-empty:first-child::before {
    color: ${({ theme }) => theme.color.neutral.color6};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  .rich-editor__prose p {
    margin: 0 0 0.5em;
  }

  .rich-editor__prose h1 {
    font-size: 1.75rem;
    margin: 0 0 0.5em;
  }

  .rich-editor__prose h2 {
    font-size: 1.4rem;
    margin: 0 0 0.5em;
  }

  .rich-editor__prose h3 {
    font-size: 1.2rem;
    margin: 0 0 0.5em;
  }

  .rich-editor__prose blockquote {
    margin: 0 0 0.5em;
    padding-left: 1em;
    border-left: 3px solid ${({ theme }) => theme.color.neutral.color7};
  }

  .rich-editor__prose ul,
  .rich-editor__prose ol {
    margin: 0 0 0.5em 1.25em;
    padding: 0;
  }

  .rich-editor__prose li {
    margin: 0.15em 0;
  }

  .rich-editor__prose a {
    color: ${({ theme }) => theme.color.status.primary};
    text-decoration: underline;
  }

  .rich-editor__prose strong {
    font-weight: ${({ theme }) => theme.font.weight.bold};
  }

  .rich-editor__prose em {
    font-style: italic;
  }

  .rich-editor__prose u {
    text-decoration: underline;
  }

  .rich-editor__prose s {
    text-decoration: line-through;
  }

  .rich-editor__prose sub,
  .rich-editor__prose sup {
    font-size: 0.75em;
  }

  .rich-editor__prose hr {
    margin: 1em 0;
    border: none;
    border-top: 1px solid ${({ theme }) => theme.color.neutral.color7};
  }
`

/**
 * TipTap-based rich text editor: styled-components + ui-kit theme tokens.
 */
export function RichEditor({
  html,
  onChangeHtml,
  readOnly = false,
  imagePlugin: _imagePlugin
}: RichEditorProps) {
  const lastEmitted = useRef<string | undefined>(undefined)
  const onChangeHtmlRef = useLatestRef(onChangeHtml)

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] }
      }),
      Subscript,
      Superscript,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank'
        }
      }),
      Placeholder.configure({
        placeholder: 'Enter text…'
      })
    ],
    []
  )

  const editor = useEditor(
    {
      extensions,
      content:
        html && html.trim()
          ? normalizeRichEditorHtmlForStorage(html)
          : emptyDoc,
      editable: !readOnly,
      editorProps: {
        attributes: {
          class: 'rich-editor__prose',
          'aria-label': 'Rich text editor'
        }
      },
      onUpdate: ({ editor: ed }) => {
        const out = normalizeRichEditorHtmlForStorage(ed.getHTML())
        lastEmitted.current = out
        onChangeHtmlRef.current?.(out)
      }
    },
    []
  )

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!readOnly)
  }, [editor, readOnly])

  useEffect(() => {
    if (!editor || html === lastEmitted.current) return
    const next = html?.trim()
      ? normalizeRichEditorHtmlForStorage(html)
      : emptyDoc
    editor.commands.setContent(next, false)
    lastEmitted.current = next
  }, [editor, html])

  return (
    <Shell>
      {editor && !readOnly ? <TipTapToolbar editor={editor} /> : null}
      <EditableWrap>
        {editor ? <EditorContent editor={editor} /> : null}
        {editor && !readOnly ? (
          <RichEditorFooterActions editor={editor} />
        ) : null}
      </EditableWrap>
    </Shell>
  )
}

export default RichEditor
